import { all, call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import { saveAs } from "file-saver";
import { GlobalEntityType, RsWorkspaceEntityType } from "shared/types";
import { RecipeScalingApiMethods } from "api/api-methods";
import logger from "utils/logger";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import { ErrorType } from "api/api-error";
import BackendClient from "api/backend-client";
import { checkForbidden, waitForLogin } from "../../auth/auth.saga";
import { addInsightFailure, addInsightSuccessful, addOptimizationFailure, addOptimizationSuccessful, addSimulationFailure, addSimulationSuccessful, editInsightFailure, editInsightSuccessful, editOptimizationFailure, editOptimizationSuccessful, editSimulationFailure, editSimulationSuccessful, exportSimulationFinish, getInsightDefaultNameFailure, getInsightDefaultNameSuccessful, getInsightOperationsFailure, getInsightOperationsSuccessful, getInsightScoreFailure, getInsightTransformationsFailure, getInsightTransformationsSuccessful, getOptimizationDefaultNameFailure, getOptimizationDefaultNameSuccessful, getSimulationDefaultNameFailure, getSimulationDefaultNameSuccessful, loadWorkspaceFailure, loadWorkspaceSuccessful, RsWorkspaceActionType, rsWorkspaceAddCellModelFailure, rsWorkspaceAddCellModelSuccessful, rsWorkspaceAddRecipeFailure, rsWorkspaceAddRecipeSuccessful, rsWorkspaceDeleteCellModelFailure, rsWorkspaceDeleteCellModelSuccessful, rsWorkspaceDeleteRecipeFailure, rsWorkspaceDeleteRecipeSuccessful, setPreviewRequest, getSimulationPreviewFailure, clearSimulationPreview, getInsightEssentialCombinationsSuccessful, getInsightEssentialCombinationsFailure, getInsightOperationDescriptionsSuccessful, getInsightOperationDescriptionsFailure, getOptimizationPreviewFailure, saveInsightTemporaryScores, setInsightValidationErrors, getOptimizationPreview, removeSimulationRelatedPreviews, persistSimulationPreview, rsWorkspaceDeleteSimulationFailure, rsWorkspaceDeleteSimulationSuccessful, rsWorkspaceDeleteInsightSuccessful, clearInsightPreview, rsWorkspaceDeleteInsightFailure, rsWorkspaceDeleteOptimizationSuccessful, clearOptimizationPreview, rsWorkspaceAddProcessDataSuccessful, rsWorkspaceAddProcessDataFailure, rsWorkspaceDeleteProcessDataSuccessful, rsWorkspaceDeleteProcessDataFailure, resetSimulationPreview, resetOptimizationPreview, resetInsightTemporaryScores, getSimulationDefaultName, clearPreviewRequest, cancelOutdatedPreview, updateSimulationScores, } from "../actions/rs-workspace.actions";
import { CancelOutdatedPreviewType, InsightInputType, ScenarioNodeType, } from "../rs-workspace.types";
import { makeUnsaved, setCurrentRequestTemplate, setDeleteTarget, setServerError, setupKnowledgeBaseItem, } from "../../knowledge-base-item/knowledge-base-item.actions";
import { connectChannelSaga, loadItemErrorHandler } from "../../knowledge-base-item/knowledge-base-item.saga";
import { RemovableEntityType, RequestTemplate, } from "../../knowledge-base-item/knowledge-base-item.types";
import { currentSimulationExportDataSelector, fetchingCurrentOptimizationPreviewDataSelector, filteredOptimizationParametersToCalculateSelector, incompleteSimulationsIdsSelector, insightsScoreDataFromCompleteSourcesSelector, isScenarioItemUsedInSimulationSelector, optimizationPreviewSelector, preloadInsightsScoresPayloadSelector, previewRequestsSelector, workspaceCellModelsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "../selectors/rs-workspace.selectors";
import { apiErrorHandler, checkConnectionSaga } from "store/global/global.saga";
import { currentItemIdentifierSelector, deleteTargetSelector, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import WSService from "api/web-socket.service";
import { InvokeRsSocketMethods } from "api/rs-socket-callbacks";
import { isSocketStartedSelector } from "../../knowledge-base-item/knowledge-base-item.selectors";
import { getInsightsScoresCalculationRequests, preformatInsightErrors } from "../utils/insights.utils";
import { InsightFormFields } from "pages/RS-Workspace/components/Scenario/components/InsightDialog";
import { getNumberFromNumberString } from "utils/string";
import { processVariablesSelector } from "store/global/global.selectors";
import { loadParameterTypes, loadProcessVariables } from "store/global/global.actions";
import { toggleNoSeatsToastVisible } from "store/licensing/licensing.actions";
import { createResultSetSaga, deleteCalculationQueueElementSaga, deleteCalculationQueueRowSaga, editCalculationQueueElementSaga, editCalculationQueueRowSaga, editCalculationQueueSaga, getAllSimulationResultsSaga, getSimulationResultSaga, moveCalculationQueueElementRowSaga, moveCalculationQueueElementSaga, moveResultSetSaga, orderResultSetElementsSaga, pauseCalculationSaga, rsWorkspaceGetRelatedToScenarioEntityResultsSaga, setProcessDataViewerSourceParamsSaga, setLineGraphSourceParamsSaga, setSpaceviewerAxisSaga, setSpaceviewerConfidenceSaga, setSpaceviewerSourceViewSaga, startCalculationSaga, stopCalculationSaga, updateResultSetElementSaga, updateResultSetSaga, updateTableElementViewSaga, upsertResultElementSaga, setSpaceviewerDensitySaga, deleteResultSetSaga, deleteResultSetElementSaga, duplicateResultSetElementSaga, duplicateResultSetSaga, handleCalculationStatusChangeSaga, exportResultElementSaga, createEntityFromResultElementSourceSaga, updatePlotSourceConfigsSaga, setPlotRangeConfigsSaga, getInitialPredictionTimeSaga, } from "./results.saga";
import { resetAllResults, RsWorkspaceResultsActionType } from "../actions/results.actions";
import { isSimulationCompleted, isSimulationVariantCompleted, isOptimizationCompleted, } from "../utils/completeness.utils";
import { noSeatsAvailableSelector } from "store/licensing/licensing.selectors";
import { isOptimizationPreviewRequestOnlyTemporary } from "../utils/common.utils";
import { waitForLicensingData } from "store/licensing/licensing.saga";
import { isNotEmpty } from "utils/validate";
import { canReCalculateOptimizationPreviewSelector } from "../selectors/completeness.selectors";
function* loadWorkspaceSaga({ payload }) {
    try {
        yield call(checkConnectionSaga);
        yield call(waitForLogin);
        yield put(loadParameterTypes());
        yield put(loadProcessVariables());
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, RecipeScalingApiMethods.GetRsWorkspace], payload);
        const { name, description, lastSavedDate, createdAt, createdBy, lastSavedBy, tags, hasUnsavedChanges, state, isLocked, isCompleted, owners, } = data;
        yield put(loadWorkspaceSuccessful({ state }));
        yield put(setupKnowledgeBaseItem({
            id: payload,
            name,
            description,
            lastSavedDate,
            createdAt,
            createdBy,
            lastSavedBy,
            tags,
            hasUnsavedChanges,
            entityType: GlobalEntityType.RsWorkspace,
            isLocked,
            isCompleted,
            owners,
        }));
        yield call(getAllSimulationResultsSaga, payload, state.resultSets);
        yield call(waitForLicensingData);
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if (noSeatsAvailable) {
            const editable = yield select(isEditableSelector);
            if (editable) {
                yield put(toggleNoSeatsToastVisible(true));
            }
        }
        yield all([
            call(rsWorkspaceGetInsightOperationsSaga),
            call(rsWorkspaceGetInsightTransformationsSaga),
            call(rsWorkspaceGetInsightEssentialCombinationsSaga),
            call(rsWorkspaceGetInsightOperationDescriptionsSaga),
        ]);
        yield call(connectChannelSaga);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace load failed");
        yield call(loadItemErrorHandler, e);
        yield put(loadWorkspaceFailure());
    }
}
function* preloadSimulationPreviewsSaga() {
    try {
        const cellModels = yield select(workspaceCellModelsSelector);
        const recipes = yield select(workspaceRecipesSelector);
        const simulations = yield select(workspaceSimulationsSelector);
        const fullyConfiguredSimulations = simulations.filter((simulation) => isSimulationCompleted(simulation, cellModels, recipes));
        for (const simulation of fullyConfiguredSimulations) {
            const completedVariants = (simulation?.state?.adjustments ?? [])?.reduce((acc, variant) => isSimulationVariantCompleted(simulation, cellModels, recipes, variant) ? [...acc, variant] : acc, [undefined]) ?? [];
            for (const variant of completedVariants) {
                yield call(fetchSimulationPreviewData, simulation.id, {
                    cellModelId: simulation.cellModelId,
                    recipeId: simulation.recipeId,
                    bioreactorId: simulation.state.bioreactorId ?? "",
                    spargerId: simulation.state.spargerId ?? "",
                    impellerId: simulation.state.impellerId ?? "",
                    adjustment: variant,
                });
            }
        }
    }
    catch (e) {
        logger.error("preload simulation previews failed");
    }
}
function* rsWorkspaceGetSimulationPreviewSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const { simulationId, ...restPayload } = payload;
        yield call(fetchSimulationPreviewData, simulationId, restPayload);
    }
    catch (e) {
        yield put(getSimulationPreviewFailure());
        yield put(setServerError({ error: e }));
        logger.error("workspace get simulation preview failed");
    }
}
function* fetchSimulationPreviewData(simulationId, data) {
    const requestId = yield call([WSService.getInstance(), "getCurrentRequestId"]);
    yield put(setPreviewRequest({
        type: InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW,
        id: requestId,
        data: { simulationId, variantId: data?.adjustment?.id ?? 0 },
    }));
    const { id: workspaceId, saved } = yield select(currentItemIdentifierSelector);
    const editMode = yield select(isEditableSelector);
    yield call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW, requestId, { elementId: simulationId, workspaceId, request: data, temporary: editMode ? !saved : false });
}
export function* recovery(id) {
    yield call(waitForLogin);
    const backendClient = BackendClient.getInstance();
    const state = yield call([backendClient, RecipeScalingApiMethods.GetRsWorkspaceTemporary], id);
    yield put(loadWorkspaceSuccessful({ state: state || null }));
    yield put(resetAllResults());
    const isSocketStarted = yield select(isSocketStartedSelector);
    if (isSocketStarted) {
        yield call(preloadWorkspacePreviewsSaga);
    }
}
function* rsWorkspaceAddCellModelSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceAddCellModel], payload);
        yield put(rsWorkspaceAddCellModelSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add cell model failed");
        yield put(rsWorkspaceAddCellModelFailure());
    }
}
function* rsWorkspaceDeleteCellModelSaga() {
    try {
        yield call(waitForLogin);
        const { id: cellModelId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.CELL_MODEL) {
            return;
        }
        const isUsedInSimulation = yield select(isScenarioItemUsedInSimulationSelector(cellModelId, ScenarioNodeType.CELLMODEL));
        yield put(setCurrentRequestTemplate(RequestTemplate.SCENARIO_CELL_MODEL_DELETE));
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceDeleteCellModel], { workspaceId, cellModelId });
        if (isUsedInSimulation) {
            yield put(clearSimulationPreview({ id: cellModelId, entityType: GlobalEntityType.CellModel }));
            yield put(removeSimulationRelatedPreviews({ id: cellModelId, entityType: GlobalEntityType.CellModel }));
        }
        yield put(rsWorkspaceDeleteCellModelSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete cell model failed");
        yield put(rsWorkspaceDeleteCellModelFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceAddRecipeSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceAddRecipe], payload);
        yield put(rsWorkspaceAddRecipeSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add recipe failed");
        yield put(rsWorkspaceAddRecipeFailure());
    }
}
function* rsWorkspaceDeleteRecipeSaga() {
    try {
        yield call(waitForLogin);
        const { id: recipeId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.RECIPE) {
            return;
        }
        const isUsedInSimulation = yield select(isScenarioItemUsedInSimulationSelector(recipeId, ScenarioNodeType.RECIPE));
        yield put(setCurrentRequestTemplate(RequestTemplate.SCENARIO_RECIPE_DELETE));
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceDeleteRecipe], { workspaceId, recipeId });
        if (isUsedInSimulation) {
            yield put(clearSimulationPreview({ id: recipeId, entityType: GlobalEntityType.Recipe }));
            yield put(removeSimulationRelatedPreviews({ id: recipeId, entityType: GlobalEntityType.Recipe }));
        }
        yield put(rsWorkspaceDeleteRecipeSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete recipe failed");
        yield put(rsWorkspaceDeleteRecipeFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceAddProcessDataSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceAddProcessData], payload);
        yield put(rsWorkspaceAddProcessDataSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add process data failed");
        yield put(rsWorkspaceAddProcessDataFailure());
    }
}
function* rsWorkspaceDeleteProcessDataSaga() {
    try {
        yield call(waitForLogin);
        const { id: processDataId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.PROCESS_DATA) {
            return;
        }
        yield put(setCurrentRequestTemplate(RequestTemplate.SCENARIO_PROCESS_DATA_DELETE));
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceDeleteProcessData], { workspaceId, processDataId });
        yield put(rsWorkspaceDeleteProcessDataSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete process data failed");
        yield put(rsWorkspaceDeleteProcessDataFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceGetSimulationNameSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const name = yield call([backendClient, RecipeScalingApiMethods.GetSimulationName], payload);
        yield put(getSimulationDefaultNameSuccessful(name));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get simulation name failed");
        yield put(getSimulationDefaultNameFailure());
    }
}
function* rsWorkspaceAddSimulationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.SIMULATION_POST));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.AddSimulation], payload);
        const simulation = data.simulations.find((item) => item.name === payload.name);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        yield put(addSimulationSuccessful({ simulationId: simulation?.id ?? null, state: data }));
        yield put(persistSimulationPreview(simulation?.id));
        yield put(getSimulationDefaultName(parseInt(workspaceId)));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add simulation failed");
        yield put(addSimulationFailure());
    }
}
function* rsWorkspaceEditSimulationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.SIMULATION_PUT));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.EditSimulation], payload);
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const insights = yield select(workspaceInsightsSelector);
        const relatedInsights = insights.filter(({ state }) => state.input.inputSets.some((item) => item.inputs.some((input) => input.inputType === InsightInputType.SIMULATION && input.inputId === payload.simulationId)));
        yield put(editSimulationSuccessful({ simulationId: payload.simulationId, state: data }));
        yield put(persistSimulationPreview(payload.simulationId));
        yield put(getSimulationDefaultName(parseInt(workspaceId)));
        const updatedInsightsScoreData = yield select(insightsScoreDataFromCompleteSourcesSelector(payload.simulationId));
        yield put(updateSimulationScores(updatedInsightsScoreData));
        yield call(rsWorkspaceRecalculateRelatedToSimulationInsightScoresSaga, payload.simulationId);
        for (const insight of relatedInsights) {
            yield call(rsWorkspaceGetRelatedToInsightOptimizationPreviewsSaga, insight.id, true);
        }
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Simulation, payload.simulationId);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace edit simulation failed");
        yield put(editSimulationFailure());
    }
}
function* rsWorkspaceDeleteSimulationSaga() {
    try {
        yield call(waitForLogin);
        const { id: simulationId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.SIMULATION) {
            return;
        }
        yield put(setCurrentRequestTemplate(RequestTemplate.SCENARIO_SIMULATION_DELETE));
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceDeleteSimulation], { workspaceId, simulationId });
        yield put(clearSimulationPreview({ id: simulationId, entityType: RsWorkspaceEntityType.Simulation }));
        const insights = yield select(workspaceInsightsSelector);
        const filteredInsights = insights.filter(({ state }) => state.input.inputSets.some((item) => item.inputs.some((input) => input.inputType === InsightInputType.SIMULATION && input.inputId === simulationId)));
        yield put(rsWorkspaceDeleteSimulationSuccessful(data));
        for (const insight of filteredInsights) {
            yield call(rsWorkspaceGetRelatedToInsightOptimizationPreviewsSaga, insight.id, true);
        }
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Simulation, simulationId);
        yield put(getSimulationDefaultName(workspaceId));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete simulation failed");
        yield put(rsWorkspaceDeleteSimulationFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceDownloadSimulationPreviewSaga({ payload }) {
    try {
        const { filename } = payload;
        const csvData = yield select(currentSimulationExportDataSelector);
        const fileToSave = new Blob([csvData], { type: "csv" });
        saveAs(fileToSave, `${filename}.csv`);
    }
    catch (e) {
        logger.error("workspace simulation preview download failed");
    }
}
function* rsWorkspaceExportSimulationSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const exportedData = yield call([backendClient, RecipeScalingApiMethods.ExportSimulation], payload);
        if (!exportedData) {
            throw new Error();
        }
        const json = JSON.stringify(exportedData);
        const fileToSave = new Blob([json], { type: "json" });
        saveAs(fileToSave, `${getCurrentDateTimeStamp()}_simulation.json`);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace export simulation failed");
    }
    finally {
        yield put(exportSimulationFinish());
    }
}
function* rsWorkspaceGetInsightNameSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const name = yield call([backendClient, RecipeScalingApiMethods.GetInsightName], payload);
        yield put(getInsightDefaultNameSuccessful(name));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get insight name failed");
        yield put(getInsightDefaultNameFailure());
    }
}
function* rsWorkspaceGetInsightOperationsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetInsightOperations,
        ]);
        yield put(getInsightOperationsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get insight operations failed");
        yield put(getInsightOperationsFailure());
    }
}
function* rsWorkspaceGetInsightTransformationsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetInsightTransformations,
        ]);
        yield put(getInsightTransformationsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get insight transformations failed");
        yield put(getInsightTransformationsFailure());
    }
}
function* rsWorkspaceGetInsightEssentialCombinationsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetInsightEssentialCombinations,
        ]);
        yield put(getInsightEssentialCombinationsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get insight essential combinations failed");
        yield put(getInsightEssentialCombinationsFailure());
    }
}
function* rsWorkspaceGetInsightOperationDescriptionsSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            RecipeScalingApiMethods.GetInsightOperationDescriptions,
        ]);
        yield put(getInsightOperationDescriptionsSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get insight operation descriptions failed");
        yield put(getInsightOperationDescriptionsFailure());
    }
}
function* rsWorkspaceFetchInsightScoreSaga(payload) {
    try {
        const outputs = payload.insightData.request.outputs;
        const requestOutput = outputs?.find(({ id }) => id === payload?.insightData?.outputId);
        const someParameterIsInvalid = requestOutput?.[InsightFormFields.operation]?.[InsightFormFields.parameters].some((item) => isNaN(getNumberFromNumberString(item.value, false)));
        if (someParameterIsInvalid) {
            return;
        }
        const requestId = yield call([WSService.getInstance(), "getCurrentRequestId"]);
        yield put(setPreviewRequest({
            type: InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW,
            id: requestId,
            data: {
                insightId: payload.insightId,
                outputId: payload.insightData.outputId,
                inputSetId: payload.insightData.inputSetId,
            },
        }));
        const { id: workspaceId, saved } = yield select(currentItemIdentifierSelector);
        const editMode = yield select(isEditableSelector);
        yield call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW, requestId, {
            elementId: payload.insightId === null ? -1 : payload.insightId,
            workspaceId,
            ...payload.insightData,
            temporary: editMode ? !saved : false,
        });
    }
    catch (e) {
        yield put(getInsightScoreFailure());
        yield put(setServerError({ error: e }));
        logger.error("workspace get insight score failed");
    }
}
function* rsWorkspaceGetInsightScoreSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield call(rsWorkspaceFetchInsightScoreSaga, payload);
    }
    catch (e) {
        yield put(getInsightScoreFailure());
        yield put(setServerError({ error: e }));
        logger.error("workspace get insight score failed");
    }
}
function* preloadInsightScoresSaga() {
    try {
        const scoreCalculationRequests = yield select(preloadInsightsScoresPayloadSelector);
        for (const insightScorePayload of scoreCalculationRequests) {
            yield call(rsWorkspaceFetchInsightScoreSaga, insightScorePayload);
        }
    }
    catch (e) {
        yield put(setServerError({ error: e }));
        logger.error("preload insight scores failed");
    }
}
function* preloadOptimizationPreviewsSaga() {
    try {
        const optimizations = yield select(workspaceOptimizationsSelector);
        for (const optimization of optimizations) {
            const canReCalculateOptimizationPreview = yield select(canReCalculateOptimizationPreviewSelector(optimization, true));
            if (canReCalculateOptimizationPreview) {
                const filteredOptimizationParameters = yield select(filteredOptimizationParametersToCalculateSelector(optimization?.state));
                yield put(getOptimizationPreview({
                    elementId: optimization?.id,
                    request: { ...optimization?.state, parameters: filteredOptimizationParameters },
                }));
            }
        }
    }
    catch (e) {
        yield put(setServerError({ error: e }));
        logger.error("preload optimization preview failed");
    }
}
function* rsWorkspaceAddInsightSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.INSIGHT_POST));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.AddInsight], payload);
        const insight = data.insights.find((item) => item.name === payload.name);
        yield put(addInsightSuccessful({ insightId: insight?.id ?? null, state: data }));
    }
    catch (e) {
        yield call(checkForbidden, e);
        const error = e.data.errors[0];
        if (!!error && error.propertyName === "") {
            /* kostyl', until RsRenamable component is refactored - currently it displays all the errors
             * which match current request template, so Insight form validation errors appear inside RsRenamable input
             */
            yield put(setServerError({ error: e }));
        }
        yield put(setInsightValidationErrors(preformatInsightErrors(e.data.errors)));
        yield put(addInsightFailure());
        logger.error("workspace add insight failed");
    }
}
function* rsWorkspaceEditInsightSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.INSIGHT_PUT));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.EditInsight], payload);
        const processVariables = yield select(processVariablesSelector);
        yield put(editInsightSuccessful({ state: data, id: payload.insightId, processVariables }));
        yield put(saveInsightTemporaryScores(payload.insightId));
        yield call(rsWorkspaceGetRelatedToInsightOptimizationPreviewsSaga, payload.insightId, true);
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Insight, payload.insightId);
    }
    catch (e) {
        yield call(checkForbidden, e);
        const error = e.data?.errors?.[0] ?? {};
        if (!!error && !isNotEmpty(error.propertyName, true)) {
            /* kostyl', until RsRenamable component is refactored - currently it displays all the errors
             * which match current request template, so Insight form validation errors appear inside RsRenamable input
             */
            yield put(setServerError({ error: e }));
        }
        else {
            yield put(setInsightValidationErrors(preformatInsightErrors(e.data.errors)));
        }
        yield put(editInsightFailure());
        logger.error("workspace edit insight failed");
    }
}
function* rsWorkspaceDeleteInsightSaga() {
    try {
        yield call(waitForLogin);
        const { id: insightId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.INSIGHT) {
            return;
        }
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const optimizations = yield select(workspaceOptimizationsSelector);
        const relatedOptimizations = optimizations.filter(({ state }) => state.input?.insightId === insightId);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.DeleteInsight], {
            workspaceId,
            insightId,
        });
        for (const optimization of relatedOptimizations) {
            yield put(clearOptimizationPreview(optimization.id));
            yield put(cancelOutdatedPreview({
                previewType: CancelOutdatedPreviewType.Optimization,
                elementId: optimization.id,
            }));
        }
        yield put(rsWorkspaceDeleteInsightSuccessful(data));
        yield put(clearInsightPreview(insightId));
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Insight, insightId);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete insight failed");
        yield put(rsWorkspaceDeleteInsightFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceGetOptimizationNameSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const name = yield call([backendClient, RecipeScalingApiMethods.GetOptimizationName], payload);
        yield put(getOptimizationDefaultNameSuccessful(name));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace get optimization name failed");
        yield put(getOptimizationDefaultNameFailure());
    }
}
function* rsWorkspaceAddOptimizationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.OPTIMIZATION_POST));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.AddOptimization], payload);
        const optimization = data.optimizations.find((item) => item.name === payload.name);
        yield put(addOptimizationSuccessful({ optimizationId: optimization?.id ?? null, state: data }));
        if (optimization?.id === undefined) {
            return;
        }
        const isPreviewInProgress = yield select(fetchingCurrentOptimizationPreviewDataSelector);
        const canReCalculateOptimizationPreview = yield select(canReCalculateOptimizationPreviewSelector(optimization, isPreviewInProgress));
        if (canReCalculateOptimizationPreview) {
            if (isPreviewInProgress) {
                yield put(cancelOutdatedPreview({ previewType: CancelOutdatedPreviewType.Optimization, elementId: -1 }));
            }
            const filteredOptimizationParameters = yield select(filteredOptimizationParametersToCalculateSelector(optimization?.state));
            yield put(getOptimizationPreview({
                elementId: optimization?.id,
                request: { ...optimization?.state, parameters: filteredOptimizationParameters },
            }));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace add optimization failed");
        yield put(addOptimizationFailure());
    }
}
function* rsWorkspaceEditOptimizationSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setCurrentRequestTemplate(RequestTemplate.OPTIMIZATION_PUT));
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.EditOptimization], payload);
        yield put(editOptimizationSuccessful({ optimizationId: payload.optimizationId, state: data }));
        const optimization = data.optimizations.find((item) => item.id === payload.optimizationId);
        const canReCalculateOptimizationPreview = yield select(canReCalculateOptimizationPreviewSelector(optimization, false));
        if (canReCalculateOptimizationPreview) {
            const filteredOptimizationParameters = yield select(filteredOptimizationParametersToCalculateSelector(optimization?.state));
            yield put(getOptimizationPreview({
                elementId: payload.optimizationId,
                request: { ...optimization?.state, parameters: filteredOptimizationParameters },
            }));
        }
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Optimization, payload.optimizationId);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace edit optimization failed");
        yield put(editOptimizationFailure());
    }
}
function* fetchOptimizationPreviewSaga(elementId, request) {
    const requestId = yield call([WSService.getInstance(), "getCurrentRequestId"]);
    yield put(setPreviewRequest({
        type: InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW,
        id: requestId,
        data: { elementId, request },
    }));
    const { id: workspaceId, saved } = yield select(currentItemIdentifierSelector);
    const editMode = yield select(isEditableSelector);
    yield call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW, requestId, {
        elementId: elementId ?? -1,
        workspaceId,
        request,
        temporary: editMode ? !saved : false,
    });
}
function* rsWorkspaceGetOptimizationPreviewSaga({ payload }) {
    try {
        yield call(waitForLogin);
        if (typeof payload.elementId === "number") {
            yield put(cancelOutdatedPreview({
                previewType: CancelOutdatedPreviewType.Optimization,
                elementId: payload.elementId,
                leavePreviewResults: true,
            }));
        }
        yield call(fetchOptimizationPreviewSaga, payload.elementId, payload.request);
    }
    catch (e) {
        yield put(getOptimizationPreviewFailure());
        logger.error("workspace get optimization preview failed");
    }
}
function* rsWorkspaceDeleteOptimizationSaga() {
    try {
        yield call(waitForLogin);
        const { id: optimizationId, type } = yield select(deleteTargetSelector);
        if (type !== RemovableEntityType.OPTIMIZATION) {
            return;
        }
        const { id: workspaceId } = yield select(currentItemIdentifierSelector);
        const backendClient = BackendClient.getInstance();
        yield put(makeUnsaved());
        const data = yield call([backendClient, RecipeScalingApiMethods.RsWorkspaceDeleteOptimization], { workspaceId, optimizationId });
        yield put(clearOptimizationPreview(optimizationId));
        yield put(rsWorkspaceDeleteOptimizationSuccessful(data));
        yield call(rsWorkspaceGetRelatedToScenarioEntityResultsSaga, RsWorkspaceEntityType.Optimization, optimizationId);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace delete optimization failed");
        yield put(rsWorkspaceDeleteSimulationFailure());
    }
    finally {
        yield put(setDeleteTarget(null));
    }
}
function* rsWorkspaceCancelTimedOutOptimizationPreviewSaga({ payload }) {
    try {
        if (!payload || payload?.errors?.[0].errorType !== ErrorType.Timeout) {
            return;
        }
        yield call(waitForLogin);
        const previewRequestsList = yield select(previewRequestsSelector);
        const previewRequest = previewRequestsList?.find(({ id, type }) => type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW && id === payload.requestId);
        if (!previewRequest) {
            return;
        }
        yield put(cancelOutdatedPreview({
            previewType: CancelOutdatedPreviewType.Optimization,
            elementId: previewRequest?.data.elementId,
        }));
    }
    catch (e) {
        logger.error("workspace cancel timed out optimization preview failed");
    }
}
function* rsWorkspaceGetRelatedToInsightOptimizationPreviewsSaga(insightId, forceOptimizationRecalculate = false) {
    try {
        const insights = yield select(workspaceInsightsSelector);
        const optimizations = yield select(workspaceOptimizationsSelector);
        const filteredOptimizations = optimizations.filter(({ state }) => state.input?.insightId === insightId);
        const processVariables = yield select(processVariablesSelector);
        const incompleteSimulationsIds = yield select(incompleteSimulationsIdsSelector);
        const simulations = yield select(workspaceSimulationsSelector);
        const cellModels = yield select(workspaceCellModelsSelector);
        const recipes = yield select(workspaceRecipesSelector);
        for (const optimization of filteredOptimizations) {
            const isCompleted = isOptimizationCompleted(optimization.state, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
            const canReCalculateOptimizationPreview = yield select(canReCalculateOptimizationPreviewSelector(optimization, forceOptimizationRecalculate));
            if (canReCalculateOptimizationPreview) {
                const filteredOptimizationParameters = yield select(filteredOptimizationParametersToCalculateSelector(optimization?.state));
                yield put(getOptimizationPreview({
                    elementId: optimization?.id,
                    request: { ...optimization?.state, parameters: filteredOptimizationParameters },
                }));
            }
            if (!isCompleted) {
                yield put(clearOptimizationPreview(optimization.id));
            }
        }
    }
    catch (e) {
        logger.error("workspace get related to insight optimization previews failed");
    }
}
function* rsWorkspaceRecalculateRelatedToSimulationInsightScoresSaga(simulationId) {
    try {
        const insights = yield select(workspaceInsightsSelector);
        const processVariables = yield select(processVariablesSelector);
        const incompleteSimulationsIds = yield select(incompleteSimulationsIdsSelector);
        const simulations = yield select(workspaceSimulationsSelector);
        const cellModels = yield select(workspaceCellModelsSelector);
        const recipes = yield select(workspaceRecipesSelector);
        const filteredInsights = insights.filter(({ state }) => state.input.inputSets.some((item) => item.inputs.some((input) => input.inputType === InsightInputType.SIMULATION && input.inputId === simulationId)));
        const scoreCalculationRequests = getInsightsScoresCalculationRequests(filteredInsights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
        for (const insightScorePayload of scoreCalculationRequests) {
            yield call(rsWorkspaceFetchInsightScoreSaga, insightScorePayload);
        }
        for (const insight of filteredInsights) {
            yield call(rsWorkspaceGetRelatedToInsightOptimizationPreviewsSaga, insight.id, true);
        }
    }
    catch (e) {
        logger.error("workspace recalculate related to simulation insight scores failed");
    }
}
function* preloadWorkspacePreviewsSaga() {
    try {
        yield call(waitForLogin);
        yield call(preloadSimulationPreviewsSaga);
        yield call(preloadInsightScoresSaga);
        yield call(preloadOptimizationPreviewsSaga);
    }
    catch (e) {
        logger.error("preload workspace previews failed");
    }
}
function* cancelOutdatedPreviewSaga({ payload }) {
    const { id: workspaceId } = yield select(currentItemIdentifierSelector);
    const previewRequests = yield select(previewRequestsSelector);
    let currentPreviewRequests = [];
    switch (payload.previewType) {
        case CancelOutdatedPreviewType.Simulation:
            currentPreviewRequests = previewRequests.filter(({ type, data }) => type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW &&
                data.simulationId === payload.elementId);
            if (!payload.leavePreviewResults) {
                yield put(resetSimulationPreview(payload.elementId));
            }
            break;
        case CancelOutdatedPreviewType.Optimization: {
            const previews = yield select(optimizationPreviewSelector);
            if (isOptimizationPreviewRequestOnlyTemporary(previews)) {
                currentPreviewRequests = previewRequests.filter(({ type, data }) => type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW &&
                    data.elementId === payload.elementId);
            }
            if (!payload.leavePreviewResults) {
                yield put(resetOptimizationPreview(payload.elementId));
            }
            break;
        }
        case CancelOutdatedPreviewType.Insight:
            currentPreviewRequests = previewRequests.filter(({ type, data }) => type === InvokeRsSocketMethods.CALCULATE_INSIGHT_PREVIEW && data.insightId === payload.elementId);
            if (!payload.leavePreviewResults) {
                yield put(resetInsightTemporaryScores(payload.elementId));
            }
            break;
    }
    if (currentPreviewRequests.length) {
        yield put(clearPreviewRequest(payload));
        yield all(currentPreviewRequests.map(({ id }) => call([WSService.getInstance(), "rsInvoke"], InvokeRsSocketMethods.CANCEL_PREVIEW, undefined, {
            workspaceId,
            previewRequestId: id,
            previewType: payload.previewType,
        })));
    }
}
export default function* workspaceSaga() {
    yield takeLatest(RsWorkspaceActionType.LOAD, loadWorkspaceSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_CELL_MODEL, rsWorkspaceAddCellModelSaga);
    yield takeLatest(RsWorkspaceActionType.DELETE_CELL_MODEL, rsWorkspaceDeleteCellModelSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_RECIPE, rsWorkspaceAddRecipeSaga);
    yield takeLatest(RsWorkspaceActionType.DELETE_RECIPE, rsWorkspaceDeleteRecipeSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_PROCESS_DATA, rsWorkspaceAddProcessDataSaga);
    yield takeLatest(RsWorkspaceActionType.DELETE_PROCESS_DATA, rsWorkspaceDeleteProcessDataSaga);
    yield takeLatest(RsWorkspaceActionType.GET_SIMULATION_DEFAULT_NAME, rsWorkspaceGetSimulationNameSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_SIMULATION, rsWorkspaceAddSimulationSaga);
    yield takeLatest(RsWorkspaceActionType.EDIT_SIMULATION, rsWorkspaceEditSimulationSaga);
    yield takeLatest(RsWorkspaceActionType.DELETE_SIMULATION, rsWorkspaceDeleteSimulationSaga);
    yield takeLatest(RsWorkspaceActionType.PRELOAD_WORKSPACE_PREVIEWS_DATA, preloadWorkspacePreviewsSaga);
    yield takeLatest(RsWorkspaceActionType.CANCEL_OUTDATED_PREVIEW, cancelOutdatedPreviewSaga);
    yield takeEvery(RsWorkspaceActionType.GET_SIMULATION_PREVIEW, rsWorkspaceGetSimulationPreviewSaga);
    yield takeLatest(RsWorkspaceActionType.DOWNLOAD_SIMULATION_PREVIEW, rsWorkspaceDownloadSimulationPreviewSaga);
    yield takeLatest(RsWorkspaceActionType.EXPORT_SIMULATION, rsWorkspaceExportSimulationSaga);
    yield takeLatest(RsWorkspaceActionType.GET_INSIGHT_DEFAULT_NAME, rsWorkspaceGetInsightNameSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_INSIGHT, rsWorkspaceAddInsightSaga);
    yield takeLatest(RsWorkspaceActionType.EDIT_INSIGHT, rsWorkspaceEditInsightSaga);
    yield takeLatest(RsWorkspaceActionType.DELETE_INSIGHT, rsWorkspaceDeleteInsightSaga);
    yield takeLatest(RsWorkspaceActionType.GET_OPTIMIZATION_DEFAULT_NAME, rsWorkspaceGetOptimizationNameSaga);
    yield takeLatest(RsWorkspaceActionType.GET_INSIGHT_SCORE, rsWorkspaceGetInsightScoreSaga);
    yield takeLatest(RsWorkspaceActionType.ADD_OPTIMIZATION, rsWorkspaceAddOptimizationSaga);
    yield takeLatest(RsWorkspaceActionType.EDIT_OPTIMIZATION, rsWorkspaceEditOptimizationSaga);
    yield takeEvery(RsWorkspaceActionType.GET_OPTIMIZATION_PREVIEW, rsWorkspaceGetOptimizationPreviewSaga);
    yield takeEvery(RsWorkspaceActionType.SET_OPTIMIZATION_PREVIEW_TIMEOUT_ERROR, rsWorkspaceCancelTimedOutOptimizationPreviewSaga);
    yield takeEvery(RsWorkspaceActionType.DELETE_OPTIMIZATION, rsWorkspaceDeleteOptimizationSaga);
    yield takeLatest(RsWorkspaceResultsActionType.CREATE_RESULT_SET, createResultSetSaga);
    yield takeLatest(RsWorkspaceResultsActionType.MOVE_RESULT_SET, moveResultSetSaga);
    yield takeLatest(RsWorkspaceResultsActionType.UPDATE_RESULT_SET, updateResultSetSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DELETE_RESULT_SET, deleteResultSetSaga);
    yield takeLatest(RsWorkspaceResultsActionType.UPSERT_RESULT_ELEMENT, upsertResultElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DELETE_RESULT_SET_ELEMENT, deleteResultSetElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.UPDATE_TABLE_ELEMENT_VIEW, updateTableElementViewSaga);
    yield takeLatest(RsWorkspaceResultsActionType.UPDATE_RESULT_SET_ELEMENT, updateResultSetElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DUPLICATE_RESULT_ELEMENT, duplicateResultSetElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DUPLICATE_RESULT_SET, duplicateResultSetSaga);
    yield takeLeading(RsWorkspaceResultsActionType.ORDER_RESULT_SET_ELEMENTS, orderResultSetElementsSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_SPACE_VIEWER_AXIS, setSpaceviewerAxisSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_SPACE_VIEWER_SOURCE_VIEW, setSpaceviewerSourceViewSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_SPACE_VIEWER_CONFIDENCE, setSpaceviewerConfidenceSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_SPACE_VIEWER_DENSITY, setSpaceviewerDensitySaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_PROCESS_DATA_VIEWER_SOURCE_PARAMS, setProcessDataViewerSourceParamsSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_LINE_GRAPH_SOURCE_PARAMS, setLineGraphSourceParamsSaga);
    yield takeEvery(RsWorkspaceResultsActionType.GET_SIMULATION_RESULT, getSimulationResultSaga);
    yield takeLatest(RsWorkspaceResultsActionType.EDIT_CALCULATION_QUEUE, editCalculationQueueSaga);
    yield takeLatest(RsWorkspaceResultsActionType.EDIT_CALCULATION_QUEUE_ROW, editCalculationQueueRowSaga);
    yield takeLatest(RsWorkspaceResultsActionType.EDIT_CALCULATION_QUEUE_ELEMENT, editCalculationQueueElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DELETE_CALCULATION_QUEUE_ROW, deleteCalculationQueueRowSaga);
    yield takeLatest(RsWorkspaceResultsActionType.DELETE_CALCULATION_QUEUE_ELEMENT, deleteCalculationQueueElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.MOVE_CALCULATION_QUEUE_ELEMENT, moveCalculationQueueElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.MOVE_CALCULATION_QUEUE_ELEMENT_ROW, moveCalculationQueueElementRowSaga);
    yield takeLatest(RsWorkspaceResultsActionType.START_CALCULATION, startCalculationSaga);
    yield takeLatest(RsWorkspaceResultsActionType.PAUSE_CALCULATION, pauseCalculationSaga);
    yield takeLatest(RsWorkspaceResultsActionType.STOP_CALCULATION, stopCalculationSaga);
    yield takeLatest(RsWorkspaceResultsActionType.HANDLE_CALCULATION_STATUS_CHANGE, handleCalculationStatusChangeSaga);
    yield takeLatest(RsWorkspaceResultsActionType.UPDATE_PLOT_SOURCE_CONFIGS, updatePlotSourceConfigsSaga);
    yield takeLatest(RsWorkspaceResultsActionType.SET_PLOT_RANGE_CONFIGS, setPlotRangeConfigsSaga);
    yield takeLatest(RsWorkspaceResultsActionType.EXPORT_RESULT_ELEMENT, exportResultElementSaga);
    yield takeLatest(RsWorkspaceResultsActionType.CREATE_ENTITY_FROM_RESULT_ELEMENT_SOURCE, createEntityFromResultElementSourceSaga);
    yield takeLatest(RsWorkspaceResultsActionType.GET_INITIAL_PREDICTION_TIME, getInitialPredictionTimeSaga);
}
